import React, { useState, useEffect } from 'react';
import { getOrders, createOrder, getWarehouseItems } from '../api';
import AddressValidator from '../components/common/AddressValidator';

function Orders({ user }) {
  const [orders, setOrders] = useState([]);
  const [warehouseItems, setWarehouseItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [orderNotes, setOrderNotes] = useState('');
  const [orderAddress, setOrderAddress] = useState('');
  const [addressValidation, setAddressValidation] = useState({ valid: false, results: [] });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024 && window.innerWidth > 768);
  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem('ordersFilters');
    if (savedFilters) {
      try {
        return JSON.parse(savedFilters);
      } catch (error) {
        console.error('Fehler beim Laden der gespeicherten Filter:', error);
      }
    }
    return {
      dateFrom: '',
      dateTo: '',
      status: '',
      productId: '',
      searchText: '', // Für ID und Name
      sortBy: 'created_at',
      sortOrder: 'desc'
    };
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  useEffect(() => {
    if (user) {
      loadOrders();
      if (user.isAgenturLeader && user.organization_id) {
        loadWarehouseItems();
      }
    }
  }, [user]);

  useEffect(() => {
    // Lade Items auch wenn das Formular geöffnet wird
    if (showCreateForm && user?.isAgenturLeader && user?.organization_id && warehouseItems.length === 0) {
      loadWarehouseItems();
    }
  }, [showCreateForm]);

  const loadOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getOrders(user.id);
      if (result.success) {
        setOrders(result.orders || []);
      } else {
        setError(result.error || 'Fehler beim Laden der Bestellungen');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadWarehouseItems = async () => {
    try {
      const result = await getWarehouseItems(user.id, user.organization_id);
      if (result.success) {
        setWarehouseItems(result.items || []);
      } else {
        console.error('Fehler beim Laden der Lagerpositionen:', result.error);
        setError(result.error || 'Fehler beim Laden der Lagerpositionen');
      }
    } catch (err) {
      console.error('Fehler beim Laden der Lagerpositionen:', err);
      setError('Netzwerkfehler beim Laden der Lagerpositionen: ' + err.message);
    }
  };

  const handleAddOrderItem = () => {
    setOrderItems([...orderItems, { warehouse_item_id: '', variant_id: '', quantity: 1 }]);
  };

  const handleRemoveOrderItem = (index) => {
    setOrderItems(orderItems.filter((_, i) => i !== index));
  };

  const handleOrderItemChange = (index, field, value) => {
    const updated = [...orderItems];
    updated[index][field] = value;
    if (field === 'warehouse_item_id') {
      updated[index].variant_id = ''; // Reset variant when item changes
      updated[index].quantity = 1; // Reset quantity
    }
    setOrderItems(updated);
  };

  const getMaxQuantity = (itemId, variantId) => {
    if (!itemId || !variantId) return null;
    const item = warehouseItems.find(wi => wi.id.toString() === itemId.toString());
    if (!item || !item.variants) return null;
    const variant = item.variants.find(v => v.id.toString() === variantId.toString());
    return variant ? (variant.available_stock || 0) : null;
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    if (orderItems.length === 0) {
      setError('Bitte fügen Sie mindestens eine Position hinzu');
      return;
    }

    // Validierung: Prüfe ob genug Stückzahl verfügbar ist
    for (const item of orderItems) {
      if (!item.warehouse_item_id || !item.quantity || item.quantity <= 0) {
        continue;
      }
      
      if (item.variant_id) {
        const maxQty = getMaxQuantity(item.warehouse_item_id, item.variant_id);
        if (maxQty !== null && parseInt(item.quantity) > maxQty) {
          setError(`Nicht genug Stückzahl verfügbar für eine Variante. Verfügbar: ${maxQty}`);
          return;
        }
      }
    }

    const validItems = orderItems
      .filter(item => item.warehouse_item_id && item.quantity > 0)
      .map(item => ({
        warehouse_item_id: parseInt(item.warehouse_item_id),
        variant_id: item.variant_id ? parseInt(item.variant_id) : null,
        quantity: parseInt(item.quantity)
      }));

    if (validItems.length === 0) {
      setError('Bitte wählen Sie gültige Positionen aus');
      return;
    }

    // Adresse validieren
    if (!orderAddress || !orderAddress.trim()) {
      setError('Bitte geben Sie eine Lieferadresse an');
      return;
    }

    // Adressvalidierung prüfen
    if (!addressValidation.valid || addressValidation.results.length === 0) {
      setError('Bitte wählen Sie eine gültige Adresse aus den Vorschlägen aus');
      return;
    }

    try {
      const result = await createOrder(user.id, validItems, orderNotes, orderAddress.trim());
      if (result.success) {
        setOrderItems([]);
        setOrderNotes('');
        setOrderAddress('');
        setAddressValidation({ valid: false, results: [] });
        setShowCreateForm(false);
        setError(''); // Fehler zurücksetzen
        loadOrders();
        loadWarehouseItems(); // Items neu laden, um aktualisierte Stückzahlen zu zeigen
      } else {
        setError(result.error || 'Fehler beim Erstellen der Bestellung');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f59e0b',
      processing: '#3b82f6',
      shipped: '#8b5cf6',
      delivered: '#10b981',
      cancelled: '#ef4444'
    };
    return colors[status] || '#64748b';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Ausstehend',
      processing: 'In Bearbeitung',
      shipped: 'Versendet',
      delivered: 'Geliefert',
      cancelled: 'Storniert'
    };
    return labels[status] || status;
  };

  // Filter-Funktionen
  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    localStorage.setItem('ordersFilters', JSON.stringify(newFilters));
  };

  const resetFilters = () => {
    const defaultFilters = {
      dateFrom: '',
      dateTo: '',
      status: '',
      productId: '',
      searchText: '',
      sortBy: 'created_at',
      sortOrder: 'desc'
    };
    setFilters(defaultFilters);
    localStorage.setItem('ordersFilters', JSON.stringify(defaultFilters));
  };

  const filterOrders = (orders) => {
    let filtered = [...orders];

    // Datum-Filter
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.created_at);
        orderDate.setHours(0, 0, 0, 0);
        return orderDate >= fromDate;
      });
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.created_at);
        return orderDate <= toDate;
      });
    }

    // Status-Filter
    if (filters.status) {
      filtered = filtered.filter(order => order.status === filters.status);
    }

    // Produkt-Filter
    if (filters.productId) {
      filtered = filtered.filter(order => {
        return order.items && order.items.some(item => 
          item.warehouse_item_id.toString() === filters.productId.toString()
        );
      });
    }

    // Suchtext-Filter (ID/Agentur)
    if (filters.searchText) {
      const searchText = filters.searchText.trim();
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(order => {
        // Suche nach Agentur-Name
        const agenturNameMatch = order.agentur_name?.toLowerCase().includes(searchLower);
        
        // Suche nach vollständiger Bestellnummer
        const orderNumberMatch = order.order_number?.toLowerCase().includes(searchLower);
        
        // Suche nach letzten 4 Ziffern der Bestellnummer
        const lastFourDigits = order.order_number?.slice(-4) || '';
        // Prüfe ob die Sucheingabe nur Zahlen enthält und mit den letzten 4 Ziffern übereinstimmt
        const isNumericSearch = /^\d+$/.test(searchText);
        // Wenn numerische Suche, prüfe ob die letzten 4 Ziffern die Sucheingabe enthalten
        const lastFourMatch = isNumericSearch && lastFourDigits.includes(searchText);
        
        return orderNumberMatch || agenturNameMatch || lastFourMatch;
      });
    }

    // Sortierung
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      if (filters.sortBy === 'created_at') {
        aValue = new Date(a.created_at);
        bValue = new Date(b.created_at);
      } else if (filters.sortBy === 'order_number') {
        aValue = a.order_number || '';
        bValue = b.order_number || '';
      } else if (filters.sortBy === 'status') {
        aValue = a.status || '';
        bValue = b.status || '';
      } else {
        aValue = a[filters.sortBy] || '';
        bValue = b[filters.sortBy] || '';
      }

      if (filters.sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    return filtered;
  };

  const filteredOrders = filterOrders(orders);

  const containerStyles = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
  };

  const headerStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  };

  const buttonStyles = {
    padding: '6px 12px', // Reduced padding
    background: '#0a9a22',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '500',
    width: isMobile ? '100%' : 'auto',
    fontSize: '13px', // Slightly smaller
  };

  const cardStyles = {
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '16px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  };

  if (loading) {
    return (
      <div style={containerStyles}>
        <div style={{ textAlign: 'center', padding: '40px' }}>Laden...</div>
      </div>
    );
  }

  return (
    <div style={containerStyles}>
      <div style={headerStyles}>
        <h1 style={{ margin: 0 }}>Meine Bestellungen</h1>
        {user.isAgenturLeader && (
          <button
            style={buttonStyles}
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? 'Abbrechen' : '+ Neue Bestellung'}
          </button>
        )}
      </div>

      {error && (
        <div style={{ padding: '12px', backgroundColor: '#fee', color: '#c33', borderRadius: '6px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {/* Filter-Bereich */}
      <div style={{ ...cardStyles, marginBottom: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '12px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Von Datum</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Bis Datum</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px' }}
            >
              <option value="">Alle</option>
              <option value="pending">Ausstehend</option>
              <option value="processing">In Bearbeitung</option>
              <option value="shipped">Versendet</option>
              <option value="delivered">Geliefert</option>
              <option value="cancelled">Storniert</option>
            </select>
          </div>
          {user.isAgenturLeader && (
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Produkt</label>
              <select
                value={filters.productId}
                onChange={(e) => handleFilterChange('productId', e.target.value)}
                style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px' }}
              >
                <option value="">Alle</option>
                {warehouseItems.map(item => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Suche (ID/Agentur)</label>
            <input
              type="text"
              value={filters.searchText}
              onChange={(e) => handleFilterChange('searchText', e.target.value)}
              placeholder="Bestellnummer, letzte 4 Ziffern oder Agentur..."
              style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Sortieren nach</label>
            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px' }}
            >
              <option value="created_at">Datum</option>
              <option value="order_number">Bestellnummer</option>
              <option value="status">Status</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Reihenfolge</label>
            <select
              value={filters.sortOrder}
              onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
              style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px' }}
            >
              <option value="desc">↓ Neu-Alt</option>
              <option value="asc">↑ Alt-Neu</option>
            </select>
          </div>
        </div>
        <button
          onClick={resetFilters}
          style={{ padding: '6px 12px', backgroundColor: '#f3f4f6', color: '#4b5563', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}
        >
          Filter zurücksetzen
        </button>
      </div>

      {showCreateForm && user.isAgenturLeader && (
        <div style={cardStyles}>
          <h3 style={{ marginTop: 0 }}>Neue Bestellung erstellen</h3>
          {warehouseItems.length === 0 && (
            <div style={{ padding: '12px', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '6px', marginBottom: '16px' }}>
              Keine Lagerpositionen verfügbar. Bitte erstellen Sie zuerst Positionen in der Lagerverwaltung.
            </div>
          )}
          <form onSubmit={handleCreateOrder}>
            {orderItems.map((item, index) => (
              <div key={index} style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr auto', gap: '8px', alignItems: 'end' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Position</label>
                    <select
                      value={item.warehouse_item_id}
                      onChange={(e) => handleOrderItemChange(index, 'warehouse_item_id', e.target.value)}
                      style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                      required
                    >
                      <option value="">Bitte wählen</option>
                      {warehouseItems && warehouseItems.length > 0 ? (
                        warehouseItems.map(wi => (
                          <option key={wi.id} value={wi.id}>{wi.name}</option>
                        ))
                      ) : (
                        <option value="" disabled>Keine Positionen verfügbar</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Variante/Größe</label>
                    <select
                      value={item.variant_id}
                      onChange={(e) => {
                        handleOrderItemChange(index, 'variant_id', e.target.value);
                        // Menge zurücksetzen wenn Variante geändert wird und neue Menge zu hoch ist
                        const maxQty = getMaxQuantity(item.warehouse_item_id, e.target.value);
                        if (maxQty !== null && parseInt(item.quantity) > maxQty) {
                          handleOrderItemChange(index, 'quantity', maxQty);
                        }
                      }}
                      style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                    >
                      <option value="">Keine Variante</option>
                      {item.warehouse_item_id && warehouseItems
                        .find(wi => wi.id.toString() === item.warehouse_item_id)
                        ?.variants?.map(v => (
                          <option key={v.id} value={v.id}>
                            {v.variant_name} ({v.variant_type}) - Verfügbar: {v.available_stock || 0} Stk.
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>
                      Menge
                      {item.variant_id && (() => {
                        const maxQty = getMaxQuantity(item.warehouse_item_id, item.variant_id);
                        return maxQty !== null ? ` (max: ${maxQty})` : '';
                      })()}
                    </label>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => {
                        const newQty = parseInt(e.target.value) || 0;
                        const maxQty = getMaxQuantity(item.warehouse_item_id, item.variant_id);
                        if (maxQty !== null && newQty > maxQty) {
                          setError(`Maximale verfügbare Menge: ${maxQty}`);
                          handleOrderItemChange(index, 'quantity', maxQty);
                        } else {
                          setError(''); // Fehler zurücksetzen wenn gültig
                          handleOrderItemChange(index, 'quantity', newQty);
                        }
                      }}
                      style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                      min="1"
                      max={item.variant_id ? getMaxQuantity(item.warehouse_item_id, item.variant_id) : undefined}
                      required
                    />
                    {item.variant_id && (() => {
                      const maxQty = getMaxQuantity(item.warehouse_item_id, item.variant_id);
                      const currentQty = parseInt(item.quantity) || 0;
                      if (maxQty !== null && currentQty > maxQty) {
                        return (
                          <div style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px' }}>
                            Verfügbar: {maxQty} Stück
                          </div>
                        );
                      }
                      if (maxQty !== null) {
                        return (
                          <div style={{ color: '#64748b', fontSize: '11px', marginTop: '4px' }}>
                            Verfügbar: {maxQty} Stück
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveOrderItem(index)}
                    style={{ padding: '8px 12px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    Entfernen
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddOrderItem}
              style={{ ...buttonStyles, backgroundColor: '#64748b', marginBottom: '12px' }}
            >
              + Position hinzufügen
            </button>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Lieferadresse <span style={{ color: '#ef4444' }}>*</span></label>
              <textarea
                value={orderAddress}
                onChange={(e) => setOrderAddress(e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '8px', 
                  border: addressValidation.valid ? '1px solid #10b981' : '1px solid #e2e8f0', 
                  borderRadius: '4px', 
                  minHeight: '100px' 
                }}
                placeholder="Straße, Hausnummer, PLZ, Ort..."
                required
              />
              <AddressValidator
                address={orderAddress}
                onValidationResult={(result) => setAddressValidation(result)}
                onAddressSelect={(selectedAddress) => {
                  setOrderAddress(selectedAddress);
                }}
              />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Notizen</label>
              <textarea
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px', minHeight: '80px' }}
                placeholder="Optionale Notizen zur Bestellung..."
              />
            </div>
            <button type="submit" style={buttonStyles}>
              Bestellung erstellen
            </button>
          </form>
        </div>
      )}

      {filteredOrders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          {orders.length === 0 ? 'Noch keine Bestellungen vorhanden.' : 'Keine Bestellungen gefunden, die den Filterkriterien entsprechen.'}
        </div>
      ) : (
        filteredOrders.map((order) => (
          <div key={order.id} style={cardStyles}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div>
                <h3 style={{ margin: '0 0 4px 0' }}>Bestellung {order.order_number}</h3>
                <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
                  Erstellt am: {new Date(order.created_at).toLocaleDateString('de-DE')}
                  {order.agentur_name && ` • Agentur: ${order.agentur_name}`}
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span
                  style={{
                    padding: '6px 12px',
                    backgroundColor: getStatusColor(order.status),
                    color: 'white',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '500',
                    whiteSpace: 'nowrap',
                    display: 'inline-block',
                  }}
                >
                  {getStatusLabel(order.status)}
                </span>
                <button
                  onClick={() => setSelectedOrder(selectedOrder === order.id ? null : order.id)}
                  style={{ padding: '6px 12px', backgroundColor: '#64748b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                >
                  {selectedOrder === order.id ? 'Ausblenden' : 'Details'}
                </button>
              </div>
            </div>

            {selectedOrder === order.id && (
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                {order.address && (
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Lieferadresse:</strong>
                    <p style={{ margin: '4px 0 0 0', color: '#64748b', whiteSpace: 'pre-line' }}>{order.address}</p>
                  </div>
                )}
                {order.tracking_number && (
                  <p style={{ marginBottom: '8px' }}>
                    <strong>Tracking-Nummer:</strong> {order.tracking_number}
                  </p>
                )}
                {order.notes && (
                  <p style={{ marginBottom: '12px', color: '#64748b' }}>
                    <strong>Notizen:</strong> {order.notes}
                  </p>
                )}
                <h4 style={{ marginBottom: '8px' }}>Bestellpositionen:</h4>
                <div>
                  {order.items && order.items.length > 0 ? (
                    order.items.map((item, index) => (
                      <div key={index} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                        <strong>{item.item_name}</strong>
                        {item.variant_name && (
                          <span style={{ marginLeft: '8px', color: '#64748b' }}>
                            ({item.variant_name})
                          </span>
                        )}
                        <span style={{ marginLeft: '12px', color: '#64748b' }}>
                          Menge: {item.quantity}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p style={{ color: '#64748b' }}>Keine Positionen gefunden</p>
                  )}
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

export default Orders;
